/**
 * WHERE a kitchen tool lights, and which lights it may take down to do it.
 *
 * Two tools light a kitchen: `style_kitchen`, and applying a kitchen layout
 * (`applyCabinetLayout`), which used to place none — its sweep cleared the
 * kitchen area's lights and nothing put any back, so a layout applied without
 * a style had nothing for the estimate to count (#2303). Both now plan with
 * `planKitchenLighting` over the same region and re-lay by the same rule.
 *
 * The region is the KITCHEN, not the room. In an open-plan room that is the
 * kitchen section (`resolveKitchenRegion`); before this `style_kitchen`
 * gridded the whole floor, 37 cans over 819 Montrose's 682 ft² great room
 * where the kitchen is ~200 ft², and took down the living and dining rooms'
 * lights to do it.
 *
 * Pure.
 */
const { resolveKitchenRegion } = require('../kitchenRegion');
const { isPointInsidePolygon } = require('../../geometry/pointInPolygon');

/**
 * Ceiling-light types kitchen lighting owns. After consolidation pendants and
 * recessed cans are both the canonical `ceiling-light` (pendants carry
 * `lightingSubtype: 'pendant'`, cans `proceduralStyle: 'recessed'`);
 * `recessed-light` / `pendant-light` are listed to also clear lights written by
 * earlier, pre-consolidation builds.
 */
const CEILING_LIGHT_TYPES = new Set([
  'ceiling-light',
  'recessed-light',
  'pendant-light',
]);

/** The two tools that lay a kitchen's lights (`planKitchenLighting`'s `source`). */
const KITCHEN_LIGHT_SOURCES = new Set(['style_kitchen', 'kitchen-layout']);

/**
 * Lights a TOOL laid out, and which a kitchen tool may therefore re-lay: its
 * own, and the structure-wide `add_lighting` grid. A light with no `addedBy`
 * was placed by hand and is never touched. The web's Design Center keeps the
 * same list (`AUTO_CEILING_LIGHT_SOURCES`, hooks/useDesignCenter.js).
 */
const AUTO_LIGHT_SOURCES = new Set([...KITCHEN_LIGHT_SOURCES, 'add_lighting']);

/**
 * The polygons a kitchen tool lights: the kitchen region's, or — when the room
 * names sections but none of them a kitchen, so there is no region to find —
 * the whole floor, which is what the tools did before they knew of regions.
 *
 * @param {object} room with `sections`, `floors`, optional `kitchenArea`
 * @returns {Array<Array<{x:number,z:number}>>} possibly empty
 */
function kitchenLightingRegions(room) {
  const { polygons } = resolveKitchenRegion(room);
  if (polygons.length) return polygons;
  const floor = room?.floors?.[0]?.polygonCorners;
  return floor && floor.length >= 3
    ? [floor.map((p) => ({ x: p.x, z: p.z }))]
    : [];
}

const isPendant = (o) =>
  o?.lightingSubtype === 'pendant' || o?.type === 'pendant-light';

/**
 * Whether a kitchen tool lighting `regions` takes this object down.
 *
 * An auto light inside the region goes. So does a pendant a kitchen tool hung,
 * WHEREVER it hangs: it belongs to an island, which is re-planned whole, and an
 * island can straddle a section boundary — leaving the old pendant there would
 * hang two over one counter. Everything else stays, including an earlier run's
 * lights outside the region: they light the rest of the room.
 */
function isReplacedKitchenLight(o, regions) {
  if (!CEILING_LIGHT_TYPES.has(o?.type) || !AUTO_LIGHT_SOURCES.has(o.addedBy)) {
    return false;
  }
  if (isPendant(o) && KITCHEN_LIGHT_SOURCES.has(o.addedBy)) return true;
  const c = o.center;
  if (!c || typeof c.x !== 'number' || typeof c.z !== 'number') return false;
  return regions.some((poly) => isPointInsidePolygon({ x: c.x, z: c.z }, poly));
}

/**
 * Replace the region's tool-laid lights with freshly planned ones.
 *
 * @param {object[]} objects the room's objects
 * @param {object[]} lights  `planKitchenLighting` output
 * @param {Array<Array<{x:number,z:number}>>} regions `kitchenLightingRegions`
 * @returns {object[]}
 */
function replaceKitchenLights(objects, lights, regions) {
  return (objects || [])
    .filter((o) => !isReplacedKitchenLight(o, regions))
    .concat(lights);
}

module.exports = {
  kitchenLightingRegions,
  replaceKitchenLights,
  isReplacedKitchenLight,
  CEILING_LIGHT_TYPES,
  KITCHEN_LIGHT_SOURCES,
  AUTO_LIGHT_SOURCES,
};
